import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@frontend/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerGhost' | 'link';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-interactive text-interactive-fg border border-interactive hover:bg-interactive-hover active:bg-interactive-active disabled:bg-surface-muted disabled:text-content-disabled disabled:border-line',
  secondary:
    'bg-surface text-content border border-line-control hover:bg-surface-hover active:bg-surface-active disabled:text-content-disabled',
  ghost:
    'bg-transparent text-content-secondary border border-transparent hover:bg-surface-hover hover:text-content disabled:text-content-disabled',
  // Solid-filled. Reserved for IRREVERSIBLE destructive actions only (delete, void, reverse) —
  // always behind a typed-confirmation modal, never presented beside a `primary` Approve button
  // in the same cluster. See agenterp.design.json colors._destructiveDecision.
  danger:
    'bg-danger text-danger-fg border border-danger hover:bg-danger-hover disabled:bg-surface-muted disabled:text-content-disabled disabled:border-line',
  // THE Reject/decline treatment for approval workflows (payroll, timesheets, expenses,
  // leave). Deliberately OUTLINE, never filled: paired against `primary`'s solid coral fill,
  // Approve and Reject differ by fill-vs-outline and by icon (check-circle-2 vs x-octagon) as
  // well as by hue, so the two survive both a glance and common colour-vision-deficiency
  // simulations. Never recolour this to a solid fill — see
  // agenterp.design.json colors._destructiveDecision for why that reintroduces the collision
  // this variant exists to prevent. Call sites should pair this with an `x-octagon` icon.
  dangerGhost:
    'bg-transparent text-error border border-error-border hover:bg-error-bg active:bg-error-bg disabled:text-content-disabled disabled:border-line disabled:bg-transparent',
  link: 'bg-transparent text-content-link border border-transparent hover:underline px-0 disabled:text-content-disabled',
};

const SIZES: Record<ButtonSize, string> = {
  // xs/sm keep their smaller VISUAL box for dense contexts (table-row actions) but reach the
  // 44px minimum target through an invisible hit-slop (the `before:` pseudo-element below)
  // rather than by growing the button — see tailwind.config.ts `minHeight.touch`.
  xs: 'relative h-control-xs px-2 text-body gap-1 rounded-sm before:absolute before:inset-x-0 before:-inset-y-2 before:content-[""]',
  sm: 'relative h-control-sm px-2.5 text-body gap-1.5 rounded-md before:absolute before:inset-x-0 before:-inset-y-[5px] before:content-[""]',
  md: 'h-control-md px-3 text-row gap-1.5 rounded-md',
  lg: 'h-control-lg px-4 text-section gap-2 rounded-lg',
};

/**
 * The button's visual recipe, for the cases that must render a real `<a>`: navigation to an
 * external system, a download, anything a middle-click or "open in new tab" has to work on.
 * A `<button>` with an onClick handler is not a link and breaks both. Shared from here so those
 * anchors keep matching the buttons beside them when the design system changes.
 */
export function buttonClasses(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  className?: string,
): string {
  return cn(
    'inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-token',
    'min-h-touch disabled:cursor-not-allowed',
    VARIANTS[variant],
    SIZES[size],
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    isLoading = false,
    leadingIcon,
    trailingIcon,
    fullWidth = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled === true || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-token',
        'min-h-touch disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {isLoading ? <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" /> : leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
});

/** Icon-only button. `label` is mandatory — an unlabelled icon button is unusable by keyboard and screen reader. */
export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'leadingIcon' | 'trailingIcon'> {
  label: string;
  icon: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 'md', className, ...rest },
  ref,
) {
  const dimension = size === 'xs' ? 'h-6 w-6' : size === 'sm' ? 'h-7 w-7' : size === 'lg' ? 'h-9 w-9' : 'h-8 w-8';
  return (
    <Button
      ref={ref}
      size={size}
      aria-label={label}
      title={label}
      className={cn('px-0', dimension, className)}
      {...rest}
    >
      {icon}
    </Button>
  );
});
